import { memo, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { use2FASetup } from '../hooks/use2FASetup';

export default function TwoFASetupPage() {
  const { isAuthed } = useAuth();
  if (!isAuthed) return <Navigate to="/login" replace />;
  return <TwoFASetupFlow />;
}

const TwoFASetupFlow = memo(function TwoFASetupFlow() {
  const {
    phase, qrCodeUrl, manualKey, code,
    error, setCode, startSetup, submitCode, reset: _reset,
  } = use2FASetup();

  // Auto-start setup when the page mounts
  useEffect(() => { startSetup(); }, [startSetup]);

  if (phase === 'enabled') {
    return (
      <div className="auth-page">
        <div className="auth-header">
          <h1 className="auth-title">2FA enabled</h1>
          <p className="auth-subtitle">
            Your account is now protected with two-factor authentication.
          </p>
        </div>
        <div className="tfa-success-banner" role="status">
          <span className="tfa-success-icon">✓</span>
          Two-factor authentication is active.
        </div>
        <p className="auth-footer">
          <a href="/settings" className="auth-footer-link">← back to settings</a>
        </p>
      </div>
    );
  }

  return (
    <div className="auth-page tfa-setup-page">
      <div className="auth-header">
        <h1 className="auth-title">set up 2FA</h1>
        <p className="auth-subtitle">
          Use Google Authenticator, Authy, or any TOTP app.
        </p>
      </div>

      {phase === 'loading_qr' && (
        <div className="tfa-loading">
          <span className="auth-spinner tfa-loading-spinner" />
          Generating secret…
        </div>
      )}

      {(phase === 'awaiting_code' || phase === 'verifying') && (
        <>
          {/* Step 1: QR Code */}
          <section className="tfa-step">
            <div className="tfa-step-label">
              <span className="tfa-step-num">1</span>
              scan this QR code
            </div>
            {qrCodeUrl && (
              <img
                className="tfa-qr"
                src={qrCodeUrl}
                alt="2FA QR code — scan with your authenticator app"
                width={200}
                height={200}
              />
            )}
            {manualKey && (
              <details className="tfa-manual">
                <summary className="tfa-manual-summary">
                  can't scan? enter manually
                </summary>
                <code className="tfa-manual-key">{manualKey}</code>
              </details>
            )}
          </section>

          {/* Step 2: Confirm code */}
          <section className="tfa-step">
            <div className="tfa-step-label">
              <span className="tfa-step-num">2</span>
              enter the 6-digit code
            </div>

            <div className="form-field">
              <label className="form-label" htmlFor="tfa-setup-code">
                authenticator code
              </label>
              <input
                id="tfa-setup-code"
                className={`form-input tfa-code-input${error ? ' form-input--error' : ''}`}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                disabled={phase === 'verifying'}
                maxLength={6}
                aria-describedby={error ? 'tfa-setup-error' : undefined}
                aria-invalid={!!error}
              />
              {error && (
                <span id="tfa-setup-error" className="form-error" role="alert">{error}</span>
              )}
            </div>

            <button
              className="btn btn--primary auth-submit-btn"
              type="button"
              onClick={submitCode}
              disabled={phase === 'verifying' || code.length !== 6}
              aria-busy={phase === 'verifying'}
            >
              {phase === 'verifying' ? (
                <span className="auth-btn-loading">
                  <span className="auth-spinner" />
                  verifying…
                </span>
              ) : (
                'enable 2FA'
              )}
            </button>
          </section>
        </>
      )}

      {error && phase === 'idle' && (
        <div className="form-server-error" role="alert">{error}</div>
      )}

      <p className="auth-footer">
        <a href="/settings" className="auth-footer-link">← back to settings</a>
      </p>
    </div>
  );
});
